import React, { useState, useEffect, useCallback } from 'react';
import {
  ShoppingBag, Trash2, Save, RefreshCw, Power,
  KeyRound, Webhook, Sparkles, Radio, ScrollText, CheckCircle2, AlertCircle,
  Smartphone, Send, Copy, Plus, ShieldCheck, ShieldOff, Edit3, Settings, Scissors
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

/* ===== Helper: growing list (always one empty row at the bottom) ===== */
function withTrailingEmpty(rows, emptyTemplate) {
  if (!rows || rows.length === 0) return [{ ...emptyTemplate, _new: true }];
  const last = rows[rows.length - 1];
  if (last._new) return rows;
  return [...rows, { ...emptyTemplate, _new: true }];
}

export default function AliExpressTab() {
  /* ===== Settings ===== */
  const [settings, setSettings] = useState({
    enabled: true,
    ai_prompt: '',
    cut_from_phrases: '',
    affiliate_app_key: '',
    affiliate_app_secret: '',
    affiliate_tracking_id: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);

  /* ===== Channels ===== */
  const [channels, setChannels] = useState([]);

  /* ===== Webhooks ===== */
  const [webhooks, setWebhooks] = useState([]);

  /* ===== Logs ===== */
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [logsRefreshing, setLogsRefreshing] = useState(false);

  /* ===== Approval Targets (Mobile App) ===== */
  const [approvalTargets, setApprovalTargets] = useState([]);

  /* ===== App Tokens ===== */
  const [appTokens, setAppTokens] = useState([]);

  /* ===== Blocked URLs Log (Netfree 418) ===== */
  const [blockedLog, setBlockedLog] = useState([]);
  const [showBlocked, setShowBlocked] = useState(false);

  /* ===== Mobile Devices ===== */
  const [devices, setDevices] = useState([]);

  /* ===== App Settings ===== */
  const [appSettings, setAppSettings] = useState({ approval_interval_minutes: 20 });
  const [settingsSaving, setSettingsSaving] = useState(false);

  /* ===== Loaders ===== */
  const loadAll = useCallback(async () => {
    try {
      const [s, c, w, t, k, d, as] = await Promise.all([
        api.get('/aliexpress/settings'),
        api.get('/aliexpress/channels'),
        api.get('/aliexpress/webhooks'),
        api.get('/aliexpress/approval-targets').catch(() => ({ data: [] })),
        api.get('/aliexpress-mobile/tokens').catch(() => ({ data: [] })),
        api.get('/aliexpress/devices').catch(() => ({ data: [] })),
        api.get('/aliexpress/settings').catch(() => ({ data: { approval_interval_minutes: 20 } }))
      ]);
      api.get('/aliexpress-mobile/blocked-log').then(r => setBlockedLog(r.data || [])).catch(() => {});
      setSettings(s.data);
      setChannels(withTrailingEmpty(c.data, { username: '', name: '', enabled: true }));
      setWebhooks(withTrailingEmpty(w.data, { name: '', webhook_url: '', method: 'POST', webhook_type: 'generic', enabled: true }));
      setApprovalTargets(withTrailingEmpty(t.data || [], { name: '', url: '', format: 'generic', method: 'POST', enabled: true }));
      setAppTokens(k.data || []);
      setDevices(d.data || []);
      setAppSettings(as.data);
    } catch (e) {
      toast.error('שגיאה בטעינת הנתונים');
    }
  }, []);

  const loadLogs = async (showToast = false) => {
    setLogsRefreshing(true);
    try {
      const res = await api.get('/aliexpress/logs?limit=200');
      setLogs(res.data);
      if (showToast) toast.success('הלוג רוענן', { duration: 800 });
    } catch (e) {
      toast.error('שגיאה בטעינת לוגים');
    } finally {
      setLogsRefreshing(false);
    }
  };

  useEffect(() => { loadAll(); }, [loadAll]);

  /* ===== Settings save ===== */
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/aliexpress/settings', settings);
      toast.success('הגדרות נשמרו');
    } catch (e) {
      toast.error(e.response?.data?.error || 'שגיאה בשמירת הגדרות');
    } finally {
      setSavingSettings(false);
    }
  };

  /* ===== Channels CRUD ===== */
  const saveChannel = async (row, index) => {
    const username = (row.username || '').replace(/^@/, '').trim();
    if (!username) return;
    try {
      if (row._new || !row.id) {
        const res = await api.post('/aliexpress/channels', {
          username,
          name: row.name || username,
          enabled: row.enabled !== false
        });
        const newRows = [...channels];
        newRows[index] = { ...row, id: res.data.id, _new: false };
        setChannels(withTrailingEmpty(newRows.filter(r => !r._new || r === newRows[index]), { username: '', name: '', enabled: true }));
        toast.success('ערוץ נוסף');
      } else {
        await api.put(`/aliexpress/channels/${row.id}`, {
          username, name: row.name, enabled: row.enabled
        });
        toast.success('ערוץ עודכן', { duration: 1000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'שגיאה בשמירת ערוץ');
    }
  };

  const deleteChannel = async (row) => {
    if (!row.id) {
      setChannels(channels.filter(r => r !== row));
      return;
    }
    if (!window.confirm('למחוק ערוץ זה?')) return;
    try {
      await api.delete(`/aliexpress/channels/${row.id}`);
      setChannels(withTrailingEmpty(channels.filter(r => r.id !== row.id), { username: '', name: '', enabled: true }));
      toast.success('ערוץ נמחק');
    } catch (e) {
      toast.error('שגיאה במחיקה');
    }
  };

  const updateChannelField = (index, field, value) => {
    const newRows = [...channels];
    newRows[index] = { ...newRows[index], [field]: value };
    // אם זו השורה האחרונה (הריקה) ויש בה ערך - הופכים אותה לפעילה ומוסיפים שורה ריקה חדשה
    if (newRows[index]._new && field === 'username' && value) {
      newRows[index]._new = false; // יישמר על blur
    }
    setChannels(withTrailingEmpty(newRows.filter((r, i) => !(r._new && i !== newRows.length - 1)), { username: '', name: '', enabled: true }));
  };

  /* ===== Webhooks CRUD ===== */
  const saveWebhook = async (row, index) => {
    const url = (row.webhook_url || '').trim();
    if (!url) return;
    try {
      if (row._new || !row.id) {
        const res = await api.post('/aliexpress/webhooks', {
          name: row.name || 'Webhook',
          webhook_url: url,
          method: row.method || 'POST',
          webhook_type: row.webhook_type || 'generic',
          enabled: row.enabled !== false
        });
        const newRows = [...webhooks];
        newRows[index] = { ...row, id: res.data.id, _new: false };
        setWebhooks(withTrailingEmpty(newRows, { name: '', webhook_url: '', method: 'POST', webhook_type: 'generic', enabled: true }));
        toast.success('Webhook נוסף');
      } else {
        await api.put(`/aliexpress/webhooks/${row.id}`, {
          name: row.name, webhook_url: url, method: row.method,
          webhook_type: row.webhook_type || 'generic', enabled: row.enabled
        });
        toast.success('עודכן', { duration: 1000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'שגיאה בשמירה');
    }
  };

  const deleteWebhook = async (row) => {
    if (!row.id) {
      setWebhooks(webhooks.filter(r => r !== row));
      return;
    }
    if (!window.confirm('למחוק webhook זה?')) return;
    try {
      await api.delete(`/aliexpress/webhooks/${row.id}`);
      setWebhooks(withTrailingEmpty(webhooks.filter(r => r.id !== row.id), { name: '', webhook_url: '', method: 'POST', webhook_type: 'generic', enabled: true }));
      toast.success('נמחק');
    } catch (e) {
      toast.error('שגיאה במחיקה');
    }
  };

  const updateWebhookField = (index, field, value) => {
    setWebhooks(prev => {
      const newRows = [...prev];
      newRows[index] = { ...newRows[index], [field]: value };
      return withTrailingEmpty(
        newRows.filter((r, i) => !(r._new && i !== newRows.length - 1)),
        { name: '', webhook_url: '', method: 'POST', webhook_type: 'generic', enabled: true }
      );
    });
  };

  const updateWebhookFields = (index, patch) => {
    setWebhooks(prev => {
      const newRows = [...prev];
      newRows[index] = { ...newRows[index], ...patch };
      return withTrailingEmpty(
        newRows.filter((r, i) => !(r._new && i !== newRows.length - 1)),
        { name: '', webhook_url: '', method: 'POST', webhook_type: 'generic', enabled: true }
      );
    });
  };

  /* ===== Approval Targets CRUD ===== */
  const updateTargetField = (index, field, value) => {
    setApprovalTargets(prev => {
      const newRows = [...prev];
      newRows[index] = { ...newRows[index], [field]: value };
      return withTrailingEmpty(
        newRows.filter((r, i) => !(r._new && i !== newRows.length - 1)),
        { name: '', url: '', format: 'generic', method: 'POST', enabled: true }
      );
    });
  };

  const saveTarget = async (row, index) => {
    const url = (row.url || '').trim();
    if (!url) return;
    try {
      if (row._new || !row.id) {
        const res = await api.post('/aliexpress/approval-targets', {
          name: row.name || 'יעד',
          url,
          format: row.format || 'generic',
          method: row.method || 'POST',
          api_secret_key: row.api_secret_key || null,
          enabled: row.enabled !== false
        });
        const newRows = [...approvalTargets];
        newRows[index] = { ...row, id: res.data.id, _new: false };
        setApprovalTargets(withTrailingEmpty(newRows, { name: '', url: '', format: 'generic', method: 'POST', enabled: true }));
        toast.success('יעד נוסף');
      } else {
        await api.put(`/aliexpress/approval-targets/${row.id}`, {
          name: row.name, url, format: row.format, method: row.method,
          api_secret_key: row.api_secret_key || null, enabled: row.enabled
        });
        toast.success('עודכן', { duration: 800 });
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'שגיאה בשמירת יעד');
    }
  };

  const deleteTarget = async (row) => {
    if (!row.id) {
      setApprovalTargets(approvalTargets.filter(r => r !== row));
      return;
    }
    if (!window.confirm('למחוק יעד זה?')) return;
    try {
      await api.delete(`/aliexpress/approval-targets/${row.id}`);
      setApprovalTargets(withTrailingEmpty(approvalTargets.filter(r => r.id !== row.id), { name: '', url: '', format: 'generic', method: 'POST', enabled: true }));
      toast.success('נמחק');
    } catch (e) {
      toast.error('שגיאה במחיקה');
    }
  };

  /* ===== App Tokens ===== */
  const createToken = async () => {
    const name = window.prompt('שם הטוקן (לדוגמה: הטלפון של יוסף):', 'phone');
    if (!name) return;
    try {
      const res = await api.post('/aliexpress-mobile/tokens', { name });
      setAppTokens([...(appTokens || []), res.data]);
      toast.success('טוקן נוצר - העתק אותו לאפליקציה');
    } catch (e) {
      toast.error('שגיאה ביצירת טוקן');
    }
  };

  const deleteToken = async (id) => {
    if (!window.confirm('למחוק טוקן זה? האפליקציה לא תוכל יותר לגשת לשרת.')) return;
    try {
      await api.delete(`/aliexpress-mobile/tokens/${id}`);
      setAppTokens(appTokens.filter(t => t.id !== id));
      toast.success('נמחק');
    } catch (e) {
      toast.error('שגיאה במחיקה');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('הועתק ללוח'),
      () => toast.error('שגיאה בהעתקה')
    );
  };

  /* ===== Devices CRUD ===== */
  const refreshDevices = async () => {
    try {
      const res = await api.get('/aliexpress/devices');
      setDevices(res.data || []);
    } catch (e) { toast.error('שגיאה בטעינת מכשירים'); }
  };

  const setDeviceStatus = async (id, status) => {
    try {
      await api.patch(`/aliexpress/devices/${id}`, { status });
      setDevices(devs => devs.map(d => d.id === id ? { ...d, status } : d));
      toast.success(status === 'approved' ? 'אושר' : status === 'blocked' ? 'נחסם' : 'עודכן');
    } catch (e) { toast.error('שגיאה'); }
  };

  const renameDevice = async (id, current) => {
    const nickname = window.prompt('כינוי למכשיר:', current || '');
    if (nickname === null) return;
    try {
      await api.patch(`/aliexpress/devices/${id}`, { nickname });
      setDevices(devs => devs.map(d => d.id === id ? { ...d, nickname } : d));
      toast.success('עודכן', { duration: 800 });
    } catch (e) { toast.error('שגיאה'); }
  };

  const deleteDevice = async (id) => {
    if (!window.confirm('למחוק את המכשיר? הוא יצטרך להירשם מחדש ולקבל אישור.')) return;
    try {
      await api.delete(`/aliexpress/devices/${id}`);
      setDevices(devs => devs.filter(d => d.id !== id));
      toast.success('נמחק');
    } catch (e) { toast.error('שגיאה'); }
  };

  const saveAppSettings = async () => {
    setSettingsSaving(true);
    try {
      await api.patch('/aliexpress/settings', {
        approval_interval_minutes: parseInt(appSettings.approval_interval_minutes)
      });
      toast.success('הגדרות נשמרו');
    } catch (e) {
      toast.error('שגיאה בשמירת הגדרות');
    } finally {
      setSettingsSaving(false);
    }
  };

  /* ===== UI ===== */
  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-l from-orange-500 to-red-500 text-white rounded-xl shadow-lg p-4 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center">
            <ShoppingBag className="w-10 h-10 ml-3 shrink-0" />
            <div>
              <h2 className="text-xl sm:text-2xl font-bold">AliExpress - שיווק שותפים</h2>
              <p className="text-sm opacity-90 mt-1">
                שלף הודעות מערוצי טלגרם, החלף קישורים בקישורי שותפים, שכתב טקסט עם AI ושלח ליעדים
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={async () => {
                try {
                  // בקשת טוקן חד-פעמי מהשרת
                  const response = await api.post('/aliexpress/app-otp');
                  const otp = response.data?.otp;
                  if (!otp) throw new Error('לא התקבל טוקן');

                  // יצירת טופס POST דינמי ושליחתו בלשונית חדשה
                  const form = document.createElement('form');
                  form.method = 'POST';
                  form.action = '/api/aliexpress/app';
                  form.target = '_blank';

                  const input = document.createElement('input');
                  input.type = 'hidden';
                  input.name = 'otp';
                  input.value = otp;
                  form.appendChild(input);

                  document.body.appendChild(form);
                  form.submit();
                  document.body.removeChild(form);
                } catch (err) {
                  console.error('Failed to open app view:', err);
                  toast.error('שגיאה בפתיחת התצוגה: ' + (err.response?.data?.error || err.message));
                }
              }}
              title="פתח תצוגת אפליקציה לאישור מוצרים — בדיוק כמו באפליקציה"
              className="flex items-center bg-white/20 hover:bg-white/30 rounded-lg px-4 py-2 transition font-medium"
            >
              <Smartphone className="w-5 h-5 ml-2" />
              פתח תצוגת אפליקציה
            </button>
            <label className="flex items-center cursor-pointer bg-white/20 hover:bg-white/30 rounded-lg px-4 py-2 transition">
              <Power className="w-5 h-5 ml-2" />
              <input
                type="checkbox"
                checked={!!settings.enabled}
                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                onBlur={saveSettings}
                className="ml-2"
              />
              <span className="font-medium">{settings.enabled ? 'מופעל' : 'כבוי'}</span>
            </label>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ===== Left: Channels + AI Prompt + Affiliate Keys ===== */}
        <div className="space-y-6">
          {/* Channels */}
          <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
            <div className="flex items-center mb-4">
              <Radio className="w-5 h-5 ml-2 text-orange-500" />
              <h3 className="text-lg font-bold">ערוצי טלגרם למעקב</h3>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              הזן username של ערוץ (ללא @). שורה חדשה תיווצר אוטומטית כשתסיים להקליד.
            </p>
            <div className="space-y-2">
              {channels.map((row, i) => (
                <div key={row.id || `new-${i}`} className="flex items-center gap-2">
                  <span className="text-gray-400 font-bold">@</span>
                  <input
                    type="text"
                    placeholder="username של הערוץ"
                    value={row.username || ''}
                    onChange={(e) => updateChannelField(i, 'username', e.target.value)}
                    onBlur={() => saveChannel(row, i)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                  />
                  <input
                    type="text"
                    placeholder="שם תצוגה (אופציונלי)"
                    value={row.name || ''}
                    onChange={(e) => updateChannelField(i, 'name', e.target.value)}
                    onBlur={() => row.id && saveChannel(row, i)}
                    className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={() => {
                      const v = !(row.enabled !== false);
                      const newRows = [...channels];
                      newRows[i] = { ...row, enabled: v };
                      setChannels(newRows);
                      if (row.id) {
                        api.put(`/aliexpress/channels/${row.id}`, { enabled: v }).catch(() => toast.error('שגיאה'));
                      }
                    }}
                    title={row.enabled !== false ? 'פעיל' : 'כבוי'}
                    className={`p-2 rounded-lg ${row.enabled !== false ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                  >
                    <Power className="w-4 h-4" />
                  </button>
                  {!row._new && (
                    <button
                      onClick={() => deleteChannel(row)}
                      className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"
                      title="מחק"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* AI prompt */}
          <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
            <div className="flex items-center mb-3">
              <Sparkles className="w-5 h-5 ml-2 text-purple-500" />
              <h3 className="text-lg font-bold">הנחיות AI לשכתוב הטקסט</h3>
            </div>
            <textarea
              value={settings.ai_prompt || ''}
              onChange={(e) => setSettings({ ...settings, ai_prompt: e.target.value })}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent text-sm font-mono"
              placeholder="כתוב כאן את ההוראות שיועברו ל-AI..."
            />
            <button
              onClick={saveSettings}
              disabled={savingSettings}
              className="mt-3 flex items-center px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition disabled:opacity-50"
            >
              <Save className="w-4 h-4 ml-2" />
              שמור הנחיות
            </button>
          </div>

          {/* Cut from phrases */}
          <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
            <div className="flex items-center mb-3">
              <Scissors className="w-5 h-5 ml-2 text-rose-500" />
              <h3 className="text-lg font-bold">מילים לחיתוך מהנקודה הזו והלאה</h3>
            </div>
            <p className="text-[11px] text-gray-500 mb-2">
              כל שורה היא ביטוי נפרד. אם אחד הביטויים מופיע בטקסט, ההודעה תיקטע לפניו <strong>לפני</strong> Gemini.
            </p>
            <textarea
              value={settings.cut_from_phrases || ''}
              onChange={(e) => setSettings({ ...settings, cut_from_phrases: e.target.value })}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-400 focus:border-transparent text-sm font-mono"
              placeholder="לדוגמה:\nלפרטים נוספים\nעוד מבצעים\nהמשך בתגובה הראשונה"
            />
            <button
              onClick={saveSettings}
              disabled={savingSettings}
              className="mt-3 flex items-center px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg transition disabled:opacity-50"
            >
              <Save className="w-4 h-4 ml-2" />
              שמור חיתוך
            </button>
          </div>

          {/* Affiliate keys */}
          <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
            <div className="flex items-center mb-3">
              <KeyRound className="w-5 h-5 ml-2 text-blue-500" />
              <h3 className="text-lg font-bold">קודי קישור שותפים</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">App Key</label>
                <input
                  type="text"
                  value={settings.affiliate_app_key || ''}
                  onChange={(e) => setSettings({ ...settings, affiliate_app_key: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">App Secret</label>
                <input
                  type="text"
                  value={settings.affiliate_app_secret || ''}
                  onChange={(e) => setSettings({ ...settings, affiliate_app_secret: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tracking ID</label>
                <input
                  type="text"
                  value={settings.affiliate_tracking_id || ''}
                  onChange={(e) => setSettings({ ...settings, affiliate_tracking_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm font-mono"
                />
              </div>
            </div>
            <button
              onClick={saveSettings}
              disabled={savingSettings}
              className="mt-3 flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition disabled:opacity-50"
            >
              <Save className="w-4 h-4 ml-2" />
              שמור קודים
            </button>
          </div>

          {/* Mobile Devices */}
          <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <Smartphone className="w-5 h-5 ml-2 text-emerald-500" />
                <h3 className="text-lg font-bold">מכשירי אפליקציה</h3>
                {devices.some(d => d.status === 'pending') && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
                    {devices.filter(d => d.status === 'pending').length} ממתינים
                  </span>
                )}
              </div>
              <button
                onClick={refreshDevices}
                className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600"
                title="רענן"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">
              כל אפליקציה שמותקנת על טלפון חדש תופיע כאן ב־<strong>ממתין</strong>. אישור — תאפשר לה לגשת לשרת. חסימה — תמנע גישה תוך 5 דקות.
            </p>
            <div className="space-y-2">
              {devices.length === 0 && <p className="text-xs text-gray-400">אין מכשירים רשומים עדיין</p>}
              {devices.map(d => (
                <div
                  key={d.id}
                  className={`rounded-lg p-3 border ${
                    d.status === 'pending' ? 'bg-amber-50 border-amber-200' :
                    d.status === 'approved' ? 'bg-emerald-50 border-emerald-200' :
                    'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        d.status === 'pending' ? 'bg-amber-200 text-amber-800' :
                        d.status === 'approved' ? 'bg-emerald-200 text-emerald-800' :
                        'bg-red-200 text-red-800'
                      }`}>
                        {d.status === 'pending' ? 'ממתין' : d.status === 'approved' ? 'מאושר' : 'חסום'}
                      </span>
                      <strong className="text-sm">{d.nickname || 'ללא כינוי'}</strong>
                      <code className="text-[10px] text-gray-500 truncate" title={d.device_id}>
                        {String(d.device_id).slice(0, 8)}…
                      </code>
                    </div>
                    <div className="flex items-center gap-1">
                      {d.status !== 'approved' && (
                        <button
                          onClick={() => setDeviceStatus(d.id, 'approved')}
                          title="אשר"
                          className="p-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg"
                        ><ShieldCheck className="w-3.5 h-3.5" /></button>
                      )}
                      {d.status !== 'blocked' && (
                        <button
                          onClick={() => setDeviceStatus(d.id, 'blocked')}
                          title="חסום"
                          className="p-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
                        ><ShieldOff className="w-3.5 h-3.5" /></button>
                      )}
                      <button
                        onClick={() => renameDevice(d.id, d.nickname)}
                        title="שנה כינוי"
                        className="p-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg"
                      ><Edit3 className="w-3.5 h-3.5" /></button>
                      <button
                        onClick={() => deleteDevice(d.id)}
                        title="מחק"
                        className="p-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="mt-1 text-[11px] text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5">
                    {d.last_ip && <span>IP: {d.last_ip}</span>}
                    {d.last_seen && <span>פעיל אחרון: {d.last_seen}</span>}
                    {d.user_agent && <span className="truncate max-w-xs" title={d.user_agent}>UA: {d.user_agent}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ===== Right: Webhooks ===== */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Webhook className="w-5 h-5 ml-2 text-green-500" />
                <h3 className="text-lg font-bold">יעדי שליחה (Webhooks)</h3>
              </div>
              <button
                onClick={loadAll}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                title="רענן"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              לכל הודעה מעובדת יישלח JSON ל-URLs הבאים. ניתן לחבר ל-Make/Zapier/n8n/אתר/בוט וכו'.
            </p>
            <div className="space-y-3">
              {webhooks.map((row, i) => (
                <div key={row.id || `new-${i}`} className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                    <input
                      type="text"
                      placeholder="שם"
                      value={row.name || ''}
                      onChange={(e) => updateWebhookField(i, 'name', e.target.value)}
                      onBlur={() => row.id && saveWebhook(row, i)}
                      className="sm:col-span-3 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder="https://example.com/webhook"
                      value={row.webhook_url || ''}
                      onChange={(e) => updateWebhookField(i, 'webhook_url', e.target.value)}
                      onBlur={() => saveWebhook(row, i)}
                      className="sm:col-span-7 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent font-mono"
                    />
                    <div className="sm:col-span-2 flex items-center justify-end gap-1">
                      <button
                        onClick={() => {
                          const v = !(row.enabled !== false);
                          const newRows = [...webhooks];
                          newRows[i] = { ...row, enabled: v };
                          setWebhooks(newRows);
                          if (row.id) {
                            api.put(`/aliexpress/webhooks/${row.id}`, { enabled: v }).catch(() => toast.error('שגיאה'));
                          }
                        }}
                        title={row.enabled !== false ? 'פעיל' : 'כבוי'}
                        className={`p-2 rounded-lg ${row.enabled !== false ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      {!row._new && (
                        <button
                          onClick={() => deleteWebhook(row)}
                          className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"
                          title="מחק"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                    <label className="sm:col-span-3 text-xs text-gray-600">סוג שליחה:</label>
                    <select
                      value={row.webhook_type || 'generic'}
                      onChange={(e) => {
                        const newType = e.target.value;
                        const patch = { webhook_type: newType };
                        if (newType === 'google_chat') patch.method = 'POST';
                        updateWebhookFields(i, patch);
                        if (row.id) {
                          api.put(`/aliexpress/webhooks/${row.id}`, patch)
                            .then(() => toast.success('סוג עודכן', { duration: 800 }))
                            .catch(() => toast.error('שגיאה בעדכון סוג'));
                        }
                      }}
                      className="sm:col-span-4 px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
                    >
                      <option value="generic">Generic JSON</option>
                      <option value="google_chat">Google Chat (תמונה + טקסט + כפתור קנייה)</option>
                    </select>
                    {row.webhook_type !== 'google_chat' && (
                      <>
                        <label className="sm:col-span-2 text-xs text-gray-600 text-left">Method:</label>
                        <select
                          value={row.method || 'POST'}
                          onChange={(e) => updateWebhookField(i, 'method', e.target.value)}
                          onBlur={() => row.id && saveWebhook(row, i)}
                          className="sm:col-span-3 px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="POST">POST</option>
                          <option value="GET">GET</option>
                          <option value="PUT">PUT</option>
                        </select>
                      </>
                    )}
                    {row.webhook_type === 'google_chat' && (
                      <span className="sm:col-span-5 text-[11px] text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        Cards v2 • POST • JSON
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2">
              <div>
                <strong>Generic JSON</strong> – נשלח לכל יעד שמקבל JSON רגיל (Make / n8n / שרת משלך):
                <pre className="mt-1 bg-white p-2 rounded overflow-x-auto text-[11px]" dir="ltr">{`{
  "channel_username": "...",
  "channel_title": "...",
  "message_id": 123,
  "text": "טקסט מעובד + קישור",
  "enhanced_text": "רק הטקסט",
  "image_url": "https://...",
  "affiliate_link": "https://...",
  "original_link": "https://...",
  "timestamp": 1700000000
}`}</pre>
              </div>
              <div>
                <strong>Google Chat</strong> – נשלח כ-Cards v2: <em>תמונה מוטמעת + טקסט שיווקי + כפתור "לקנייה באלי אקספרס"</em> בלבד. שיים את ה-URL של ה-webhook של מרחב Google Chat (Manage webhooks):
                <pre className="mt-1 bg-white p-2 rounded overflow-x-auto text-[11px]" dir="ltr">{`{ "cardsV2": [{ "card": { "sections": [{ "widgets": [
  { "image": { "imageUrl": "..." } },
  { "textParagraph": { "text": "..." } },
  { "buttonList": { "buttons": [{ "text": "לקנייה באלי אקספרס",
      "onClick": { "openLink": { "url": "<affiliate>" } } }] } }
]}]}}]}`}</pre>
              </div>
            </div>
          </div>

          {/* Mobile App: Approval Targets + Tokens */}
          <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <Smartphone className="w-5 h-5 ml-2 text-indigo-500" />
                <h3 className="text-lg font-bold">אפליקציית אנדרואיד — יעדי אישור</h3>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              לאחר אישור הודעה באפליקציה, היא תישלח לכל היעדים המסומנים פעיל. ניתן לבחור פורמט: JSON רגיל או Google Chat (Cards v2 עם תמונה+טקסט+כפתור קנייה).
            </p>

            {/* Targets list */}
            <div className="space-y-2 mb-4">
              {approvalTargets.map((row, i) => (
                <div key={row.id || `tnew-${i}`} className="bg-indigo-50/40 rounded-lg p-3 border border-indigo-100 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                    <input
                      type="text"
                      placeholder="שם"
                      value={row.name || ''}
                      onChange={(e) => updateTargetField(i, 'name', e.target.value)}
                      onBlur={() => row.id && saveTarget(row, i)}
                      className="sm:col-span-3 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder="https://chat.googleapis.com/... או webhook אחר"
                      value={row.url || ''}
                      onChange={(e) => updateTargetField(i, 'url', e.target.value)}
                      onBlur={() => saveTarget(row, i)}
                      className="sm:col-span-7 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-transparent font-mono"
                    />
                    <div className="sm:col-span-2 flex items-center justify-end gap-1">
                      <button
                        onClick={() => {
                          const v = !(row.enabled !== false);
                          const newRows = [...approvalTargets];
                          newRows[i] = { ...row, enabled: v };
                          setApprovalTargets(newRows);
                          if (row.id) {
                            api.put(`/aliexpress/approval-targets/${row.id}`, { enabled: v }).catch(() => toast.error('שגיאה'));
                          }
                        }}
                        title={row.enabled !== false ? 'פעיל' : 'כבוי'}
                        className={`p-2 rounded-lg ${row.enabled !== false ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      {!row._new && (
                        <button
                          onClick={() => deleteTarget(row)}
                          className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"
                          title="מחק"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                    <label className="sm:col-span-3 text-xs text-gray-600">פורמט שליחה:</label>
                    <select
                      value={row.format || 'generic'}
                      onChange={(e) => {
                        const newFormat = e.target.value;
                        const patch = { format: newFormat };
                        if (newFormat === 'google_chat' || newFormat === 'website') patch.method = 'POST';
                        const newRows = [...approvalTargets];
                        newRows[i] = { ...row, ...patch };
                        setApprovalTargets(newRows);
                        if (row.id) {
                          api.put(`/aliexpress/approval-targets/${row.id}`, patch)
                            .catch(() => toast.error('שגיאה בעדכון פורמט'));
                        }
                      }}
                      className="sm:col-span-5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
                    >
                      <option value="generic">Generic JSON (Make/Zapier/n8n/שרת משלך)</option>
                      <option value="google_chat">Google Chat (Cards v2 - תמונה + טקסט + כפתור)</option>
                      <option value="website">שליחה לאתר (POST · X-API-Key · text/author/timestamp)</option>
                    </select>
                    {row.format !== 'google_chat' && row.format !== 'website' && (
                      <>
                        <label className="sm:col-span-2 text-xs text-gray-600 text-left">Method:</label>
                        <select
                          value={row.method || 'POST'}
                          onChange={(e) => updateTargetField(i, 'method', e.target.value)}
                          onBlur={() => row.id && saveTarget(row, i)}
                          className="sm:col-span-2 px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="POST">POST</option>
                          <option value="GET">GET</option>
                          <option value="PUT">PUT</option>
                        </select>
                      </>
                    )}
                  </div>
                  {row.format === 'website' && (
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                      <label className="sm:col-span-3 text-xs text-gray-600">API Key (אופציונלי):</label>
                      <input
                        type="text"
                        placeholder="יישלח ב-Header בשם X-API-Key"
                        value={row.api_secret_key || ''}
                        onChange={(e) => updateTargetField(i, 'api_secret_key', e.target.value)}
                        onBlur={() => row.id && saveTarget(row, i)}
                        className="sm:col-span-9 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-transparent font-mono"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Tokens panel */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <KeyRound className="w-4 h-4 ml-2 text-indigo-500" />
                  <h4 className="font-bold text-sm">טוקני גישה לאפליקציה</h4>
                </div>
                <button
                  onClick={createToken}
                  className="flex items-center gap-1 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-xs"
                >
                  <Plus className="w-3 h-3" /> צור טוקן חדש
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mb-2">
                ללא טוקנים — הגישה לאפליקציה פתוחה (לא מומלץ). כדי להגביל, צור טוקן והכנס אותו בהגדרות האפליקציה.
              </p>
              <div className="space-y-1">
                {appTokens.length === 0 && <p className="text-xs text-gray-400">אין טוקנים — צור טוקן חדש להגנה</p>}
                {appTokens.map(t => (
                  <div key={t.id} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2 text-xs">
                    <span className="font-bold w-24 truncate">{t.name || 'app'}</span>
                    <code className="flex-1 font-mono truncate text-gray-700" title={t.token}>{t.token}</code>
                    <button onClick={() => copyToClipboard(t.token)} title="העתק" className="p-1 hover:bg-gray-200 rounded">
                      <Copy className="w-3 h-3" />
                    </button>
                    <button onClick={() => deleteToken(t.id)} title="מחק" className="p-1 hover:bg-red-100 rounded text-red-500">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Netfree Blocked URLs Log (418) */}
          <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 ml-2 text-red-500" />
                <h3 className="text-lg font-bold">חסימות נטפרי (418)</h3>
                {blockedLog.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                    {blockedLog.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowBlocked(!showBlocked); if (!showBlocked) api.get('/aliexpress-mobile/blocked-log').then(r => setBlockedLog(r.data || [])).catch(() => {}); }}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                >
                  {showBlocked ? 'הסתר' : 'הצג'}
                </button>
                {showBlocked && (
                  <button
                    onClick={() => api.get('/aliexpress-mobile/blocked-log').then(r => setBlockedLog(r.data || [])).catch(() => toast.error('שגיאה'))}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs"
                  >
                    <RefreshCw className="w-3 h-3" /> רענן
                  </button>
                )}
                {showBlocked && blockedLog.length > 0 && (
                  <button
                    onClick={async () => {
                      if (!window.confirm('לנקות את לוג החסימות?')) return;
                      try { await api.delete('/aliexpress-mobile/blocked-log'); setBlockedLog([]); toast.success('נוקה'); }
                      catch (e) { toast.error('שגיאה'); }
                    }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg text-xs"
                  >
                    <Trash2 className="w-3 h-3" /> נקה
                  </button>
                )}
              </div>
            </div>
            {showBlocked && (
              <>
                <p className="text-[11px] text-gray-500 mb-2">
                  כתובות שנחסמו ע"י נטפרי (קוד 418) — מהאפליקציה ומהשרת. שחרר אותן בלוח הבקרה של נטפרי.
                </p>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {blockedLog.length === 0 && <p className="text-xs text-gray-400">אין חסימות מתועדות</p>}
                  {blockedLog.map(b => (
                    <div key={b.id} className="flex items-center gap-2 bg-red-50/50 rounded-lg p-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded font-bold ${b.source === 'app' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {b.source === 'app' ? 'אפליקציה' : 'שרת'}
                      </span>
                      <code className="flex-1 font-mono truncate text-gray-700" title={b.url}>{b.url}</code>
                      {b.device_nickname && <span className="text-gray-500 truncate max-w-[120px]">{b.device_nickname}</span>}
                      <span className="text-gray-400 whitespace-nowrap">{new Date(b.created_at + 'Z').toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Settings */}
          <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <Settings className="w-5 h-5 ml-2 text-gray-500" />
                <h3 className="text-lg font-bold">הגדרות אפליקציה</h3>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  פרק זמן בין אישורים (דקות)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    value={appSettings.approval_interval_minutes || 20}
                    onChange={(e) => setAppSettings({...appSettings, approval_interval_minutes: e.target.value})}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <button
                    onClick={saveAppSettings}
                    disabled={settingsSaving}
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg disabled:opacity-50"
                  >
                    {settingsSaving ? 'שומר...' : 'שמור'}
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  כל כמה זמן לאשר הודעה אחת מהתור - ברירת מחדל 20 דקות
                </p>
              </div>
            </div>
          </div>

          {/* Logs */}
          <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ScrollText className="w-5 h-5 ml-2 text-gray-500" />
                <h3 className="text-lg font-bold">לוג פעילות AliExpress</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowLogs(!showLogs); if (!showLogs) loadLogs(); }}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                >
                  {showLogs ? 'הסתר' : 'הצג'}
                </button>
                {showLogs && (
                  <button
                    onClick={() => loadLogs(true)}
                    disabled={logsRefreshing}
                    title="רענן לוג"
                    className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${logsRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                )}
              </div>
            </div>
            {showLogs && (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {logs.length === 0 && <p className="text-sm text-gray-400">אין רשומות עדיין</p>}
                {logs.map((l) => (
                  <div key={l.id} className={`p-3 rounded-lg border text-xs ${
                    l.status === 'success' ? 'bg-green-50 border-green-200' :
                    l.status === 'rejected' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {l.status === 'success'
                          ? <CheckCircle2 className="w-4 h-4 text-green-500" />
                          : <AlertCircle className="w-4 h-4 text-yellow-500" />}
                        <strong>{l.action}</strong>
                        <span className="text-gray-500">@{l.channel_username} • #{l.message_id}</span>
                      </div>
                      <span className="text-gray-400">{l.timestamp}</span>
                    </div>
                    {l.original_text && (
                      <details className="mt-1"><summary className="cursor-pointer text-gray-600">טקסט מקורי</summary>
                        <pre className="whitespace-pre-wrap mt-1 bg-white p-2 rounded">{l.original_text}</pre>
                      </details>
                    )}
                    {l.enhanced_text && (
                      <details className="mt-1"><summary className="cursor-pointer text-gray-600">טקסט מעובד</summary>
                        <pre className="whitespace-pre-wrap mt-1 bg-white p-2 rounded">{l.enhanced_text}</pre>
                      </details>
                    )}
                    {l.affiliate_link && (
                      <div className="mt-1 text-blue-600 break-all">→ {l.affiliate_link}</div>
                    )}
                    {l.error && <div className="mt-1 text-red-600">{l.error}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
